import { useState, useRef, useEffect } from 'react';
import { getTickerInfo, getTickerAudioUrl, hasFullCerebroAnalysis } from '../lib/tickerInfo';

/**
 * TickerPopover — Popup ligero que explica un ticker (ETF o accion) cuando
 * el usuario lo toca en la composicion del portafolio.
 *
 * Muestra: ticker + nombre + descripcion + audio ~20s + (opcional) boton
 * "Ver analisis completo" si el ticker esta en el catalogo del Cerebro 2.0.
 *
 * UX: bottom sheet en movil, modal centrado en desktop, ESC para cerrar,
 * click fuera para cerrar.
 *
 * Props:
 *   symbol            string — ticker a explicar (ej. 'VOO', 'QQQ')
 *   onClose           function — callback al cerrar
 *   onSeeFullAnalysis function — callback opcional para "Ver analisis completo"
 *                                (si no se pasa, no se muestra el boton)
 */
export default function TickerPopover({ symbol, onClose, onSeeFullAnalysis }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const info = getTickerInfo(symbol);
  const audioUrl = getTickerAudioUrl(symbol);
  const hasAnalysis = hasFullCerebroAnalysis(symbol);

  // ESC para cerrar
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Auto-pause cuando se desmonta
  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const handlePlayToggle = () => {
    if (!audioRef.current) return;
    if (isPlaying) audioRef.current.pause();
    else audioRef.current.play().catch(() => {});
  };

  const handleSeeAnalysis = () => {
    onClose();
    if (onSeeFullAnalysis) onSeeFullAnalysis(symbol);
  };

  if (!info) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header con gradient navy */}
        <div className="rounded-t-2xl p-5 text-white" style={{ background: 'linear-gradient(135deg, #0a2540 0%, #1a3a6c 100%)' }}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5 font-semibold">
                {info.kind === 'etf' ? 'Fondo cotizado · ETF' : 'Acción individual'}
              </div>
              <div className="text-2xl font-bold leading-tight">{symbol.toUpperCase()}</div>
              <div className="text-sm opacity-90 mt-0.5">{info.name}</div>
            </div>
            <button
              onClick={onClose}
              aria-label="Cerrar"
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white text-xl flex-shrink-0"
              type="button"
            >
              ×
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5">
          {/* Descripcion */}
          <p className="text-sm text-[#374151] leading-relaxed mb-4">{info.what}</p>

          {/* Audio narrado */}
          <div className="bg-gradient-to-br from-[#0a2540] to-[#1a3a6c] rounded-lg p-3 mb-4 text-white">
            <div className="flex items-center gap-3">
              <button
                onClick={handlePlayToggle}
                className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition flex-shrink-0"
                aria-label={isPlaying ? 'Pausar' : 'Reproducir'}
                type="button"
              >
                {isPlaying ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><rect x="6" y="5" width="4" height="14"/><rect x="14" y="5" width="4" height="14"/></svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
                )}
              </button>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider opacity-70">Escuchar explicación</div>
                <div className="text-xs opacity-90">Narrado por el Oráculo · ~20 seg</div>
              </div>
            </div>
            <audio
              ref={audioRef}
              src={audioUrl}
              preload="metadata"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
              style={{ display: 'none' }}
            />
          </div>

          {/* Boton ver analisis completo (solo si esta en Cerebro catalog) */}
          {hasAnalysis && onSeeFullAnalysis && (
            <button
              onClick={handleSeeAnalysis}
              className="w-full py-3 bg-[#2A59C6] text-white rounded-xl text-sm font-semibold hover:bg-[#1E4BB8] transition flex items-center justify-center gap-2"
              type="button"
            >
              Ver análisis completo del Oráculo →
            </button>
          )}

          {!hasAnalysis && (
            <div className="text-[11px] text-[#9CA3AF] text-center mt-1">
              Próximamente análisis completo del Oráculo para este activo
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
